import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatMenuTrigger, MatMenu } from '@angular/material/menu';
import { Router } from '@angular/router';
import { DynamicDropdownService } from '@app/services/dynamic-dropdown.service';
import { TourGuideService } from '@app/services/tour-guide.service';
import { Subscription, of } from 'rxjs';
import dummyData from '@app/assets/api-dummy-data.json';
import { GITHUB_URL } from '@app/src/util/common-utils';

@Component({
  selector: 'app-dynamic-recursive-menu',
  templateUrl: './dynamic-recursive-menu.component.html',
  styleUrls: ['./dynamic-recursive-menu.component.scss']
})

export class DynamicRecursiveMenuComponent implements OnInit, AfterViewInit {

  /****************** Variable declaration ******************/
  menuMap = new Map<string, MatMenu>();
  currentSubMenuTrigger: MatMenuTrigger | null = null;
  dataSubscription: Subscription;
  resetSearchSubscription: Subscription;
  page: any;
  children: { [key: string]: any[] | null } = {};
  searchTerm = '';

  /****************** View child ******************/
  @ViewChildren(MatMenu) subMenus!: QueryList<MatMenu>;

  /****************** Input and Output decorators ******************/
  @Input() data: any;
  @Input() path!: string[];
  @Input() mainMenuTrigger!: MatMenuTrigger;
  @Input() isWorkflowConfig: boolean;
  @Input() childCntrl: FormControl;

  @Output() itemSelected = new EventEmitter<any>();


  /**
   * Constructor
   * @param dynamicDropService 
   * @param router 
   */
  constructor(public dynamicDropService: DynamicDropdownService,
    public router: Router,
    public guideService: TourGuideService) {
    this.dataSubscription = this.dynamicDropService.getBoolData().subscribe(data => {
      this.page = data;
    });

    this.dataSubscription = this.guideService.data$.subscribe((res: any) => {
      if (this.guideService.isStartTour) {
        if (res.page == 'config') {
          this.childCntrl.setValue('$Component Build')
        }
      }
    })

    // Reset this instance's search whenever the dropdown is reopened
    this.resetSearchSubscription = this.dynamicDropService.onResetSearches().subscribe(() => {
      this.searchTerm = '';
      this.children = {};
    });
  }

  /** ngOnInit */
  ngOnInit(): void {
    if ('/github-actions'.includes(this.router.url)) {
      this.childCntrl.setValue('All')
    }
  }

  /** ngAfterViewInit */
  ngAfterViewInit(): void {
    this.subMenus.forEach((menu, index) => {
      const key = `subMenu_${index}`;
      this.menuMap.set(key, menu);
    });
  }

  /** ngOnDestroy - clean up subscriptions */
  ngOnDestroy(): void {
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }
    if (this.resetSearchSubscription) {
      this.resetSearchSubscription.unsubscribe();
    }
  }

  /** Get the keys of an object */
  getObjectKeys(obj: any): string[] {
    if (obj === null || obj === undefined) {
      return [];
    }
    if (Array.isArray(obj) || typeof obj === 'object') {
      return Object.keys(obj);
    }
    return [];
  }

  /** Check if a value has child nodes */
  hasChildNodes(value: any): boolean {
    return this.isObject(value) || this.isArray(value);
  }

  /** Check if a value is an object */
  isObject(value: any): boolean {
    return typeof value === 'object' && value !== null;
  }

  /** Check if a value is an array */
  isArray(value: any): boolean {
    return Array.isArray(value);
  }

  /** Check if a value is a string */
  isString(val: any): boolean {
    return typeof val === 'string';
  }

  /** Close the current submenu */
  closeSubMenu(): void {
    if (this.currentSubMenuTrigger) {
      this.currentSubMenuTrigger!.closeMenu();
      this.currentSubMenuTrigger = null;
    }
  }

  /** Close the main menu */
  closeMainMenu(): void {
    let box = document.getElementsByClassName('cdk-overlay-container');
    const boxElements = Array.from(box) as HTMLElement[];

    boxElements.forEach(ele => {
      ele.classList.add('displayBlock')
    });
  }

  /** Open a submenu */
  openSubMenu(subMenuTrigger: MatMenuTrigger, event: Event, key: string): void {   
    event.stopPropagation();
    this.closeSubMenu();
    this.currentSubMenuTrigger = subMenuTrigger;
    
    if (!this.children[key]) {
      this.children[key] = null;
      setTimeout(() => {
        this.children[key] = this.isArray(this.data[key]) ? this.data[key] : [this.data[key]];
      });
    }

    subMenuTrigger.openMenu();
  }

  /** Handle menu item selection */
  menuItemSelected(path: string[], isArrayString: boolean, submenuKey: string): void {
    let newPath: any;
    if (!isArrayString) {
      path.pop();
    }
    this.closeSubMenu();
    if (this.page?.isGithubDrpdn) {
      newPath = path.length > 1 ? `${path.join('.')}` : `${path[0]}`;
    } else {
      newPath = path.length > 1 ? `$${path.join('.')}` : `$${path[0]}`;
    }
    this.childCntrl.setValue(newPath);

    if (this.page?.isWorkflowCngDrpdn) {
      // this.dynamicDropService.sendData({ key: newPath, value: this.data[submenuKey], org: path[0], repoUrl: GITHUB_URL + path[0] + `/` + path[1], branchName: path[2] });
      this.itemSelected.emit({ key: newPath, value: this.data[submenuKey], org: path[0], repoUrl: GITHUB_URL + path[0] + `/` + path[1], branchName: path[2] });
    } else if (this.page?.isGithubDrpdn) {
      this.dynamicDropService.sendData({ key: newPath, repo: path[1], branch: path[2], all: path[0] });
    }
  }

  /**
   * It will returns object if key is a string or else directly it will return key
   * @param key 
   * @returns 
   */
  checkItem(key: any) {
    if (this.isString(key)) {
      let convertoArr = [key];
      let newObj = convertoArr.reduce((newKey: any, value: any) => ({ ...newKey, [value]: value }), {})
      return newObj;
    } else {
      return key
    }
  }

  /** Tool tip function is used to display the tool tip only if toolTip input is true */
  getToolTip(key: any) {
    return this.page?.isWorkflowCngDrpdn ? key : '';
  }

  /** Change display name to replace some string with empty string */
  changeDisplayNm(key: any) {
    if (key.includes('github.com/GM-GPD-DevTest/')) {
      return key.replace('github.com/GM-GPD-DevTest/', '')
    } else if (key.includes('.github/workflows/')) {
      return key.replace('.github/workflows/', '')
    }
    return key
  }

  /** Triggered when the user types in the root-level search input */
  onSearchChange(term: string): void {
    this.searchTerm = term || '';
    // Reset cached children so submenus rebuild against the filtered tree on next open
    this.children = {};
  }

  /** Clear the root-level search */
  clearSearch(): void {
    this.searchTerm = '';
    this.children = {};
  }

  /**
   * Returns the keys to render at the current level. Keys are filtered by
   * the local search term using a case-insensitive **prefix** match against
   * the key's displayed name (i.e. the name must START WITH the search
   * term). Each recursive instance has its own `searchTerm`, so every
   * submenu has independent filtering.
   */
  getFilteredKeys(): string[] {
    const keys = this.getObjectKeys(this.data);
    const lower = this.searchTerm && this.searchTerm.trim()
      ? this.searchTerm.trim().toLowerCase()
      : '';
    const filtered = lower
      ? keys.filter(k => {
          const display = this.changeDisplayNm(k) ?? k;
          return String(display).toLowerCase().startsWith(lower);
        })
      : keys;
    // Sort alphabetically (case-insensitive) based on the displayed name
    return filtered.slice().sort((a, b) => {
      const da = String(this.changeDisplayNm(a) ?? a).toLowerCase();
      const db = String(this.changeDisplayNm(b) ?? b).toLowerCase();
      return da.localeCompare(db);
    });
  }

  /**
   * Merge an array of child items (objects and/or strings) into a single
   * object so a submenu can be rendered by ONE recursive component
   * (and therefore have exactly one search box at its top).
   */
  mergeItems(items: any[]): any {
    const result: any = {};
    if (!Array.isArray(items)) {
      return this.checkItem(items);
    }
    items.forEach(item => {
      const obj = this.checkItem(item);
      if (obj && typeof obj === 'object') {
        Object.keys(obj).forEach(k => {
          result[k] = obj[k];
        });
      }
    });
    return result;
  }

}
